"use client";

import { useRef } from "react";
import { useReducedMotion } from "motion/react";
import { useLazyVideo } from "@/components/motion/useLazyVideo";
import styles from "./PrototypeClip.module.scss";

interface PrototypeClipProps {
  src: string;
  poster?: string;
  caption?: string;
  /** CSS aspect-ratio of the clip, e.g. "4 / 5" for phone footage. */
  aspect?: string;
  /** Run the clip across the whole text column instead of the small aside. */
  wide?: boolean;
}

/**
 * A small captioned clip for inside an MDX section: raw prototype or
 * testing footage that should sit under the text, not carry the page.
 * Always below the fold, so the file attaches as the clip nears the
 * viewport and pauses once it's scrolled away (useLazyVideo).
 */
export function PrototypeClip({
  src,
  poster,
  caption,
  aspect = "4 / 5",
  wide = false,
}: PrototypeClipProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const videoRef = useRef<HTMLVideoElement>(null);

  useLazyVideo(videoRef, src, { mode: "visible", reduceMotion });

  return (
    <figure className={wide ? `${styles.clip} ${styles.wide}` : styles.clip}>
      <div className={styles.frame} style={{ aspectRatio: aspect }}>
        <video
          ref={videoRef}
          poster={poster}
          muted
          loop
          playsInline
          controls
          preload="none"
          aria-label={caption ?? "Prototype clip"}
        />
      </div>
      {caption && (
        <figcaption className={`oi-xs oi-muted ${styles.caption}`}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
